import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import ShiftAssignmentForm from "./ShiftAssignmentForm";
import ScheduleGenerationPanel from "./ScheduleGenerationPanel";
import ScheduleAvailabilityForm from "./ScheduleAvailabilityForm";
import ScheduleAbsenceForm from "./ScheduleAbsenceForm";
import ScheduleUnexcusedAbsenceForm from "./ScheduleUnexcusedAbsenceForm";
import WorkforcePlanningPanel from "./WorkforcePlanningPanel";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import Employee from "../admin/employees/Employee";

const statusLabels = {
  DRAFT: "Roboczy",
  PUBLISHED: "Opublikowany",
  ARCHIVED: "Archiwalny"
};

const assignmentStatusLabels = {
  PLANNED: "Zaplanowana",
  OPEN: "Otwarta",
  CANCELLED: "Anulowana"
};

const assignmentStatusDescriptions = {
  PLANNED: "Zmiana ma przypisanego pracownika i stanowi część jego grafiku pracy.",
  OPEN: "Brak obsady. To miejsce wynikające z zapotrzebowania, do którego nie został jeszcze przypisany pracownik.",
  CANCELLED: "Zmiana została anulowana. Pozostaje w historii, ale nie jest aktywną zmianą pracownika."
};

const assignmentSourceLabels = {
  MANUAL: "Ręcznie",
  GENERATED: "Generator",
  SWAP: "Wymiana",
  OPEN_SHIFT: "Otwarta zmiana"
};

const assignmentSourceDescriptions = {
  MANUAL: "Pozycja została dodana ręcznie albo była później ręcznie zmieniana przez osobę układającą grafik.",
  GENERATED: "Pozycja została automatycznie utworzona przez generator na podstawie planu zapotrzebowania.",
  SWAP: "Pozycja pochodzi z procesu wymiany zmiany między pracownikami.",
  OPEN_SHIFT: "Pozycja została utworzona jako niewypełnione zapotrzebowanie, ponieważ generator nie znalazł pracownika możliwego do przydzielenia."
};

const HIDE_UNASSIGNED_STORAGE_KEY = "qstaff.schedule.hideUnassignedEmployees";

const costRateSourceLabels = {
  MANUAL: "Ręczna",
  EMPLOYEE_POSITION: "Pracownik / stanowisko",
  CONTRACT: "Umowa",
  POSITION: "Stanowisko"
};

const columnHelp = {
  status: (
    <div className="schedule-tooltip-content">
      <strong>Status konkretnej zmiany</strong>
      <span><b>Zaplanowana</b> — ma przypisanego pracownika i jest częścią jego grafiku.</span>
      <span><b>Otwarta</b> — brak obsady; to jeszcze nie jest zmiana żadnego pracownika.</span>
      <span><b>Anulowana</b> — pozycja historyczna, która nie jest aktywną zmianą.</span>
    </div>
  ),
  source: (
    <div className="schedule-tooltip-content">
      <strong>Sposób powstania pozycji</strong>
      <span><b>Generator</b> — automatycznie z planu zapotrzebowania.</span>
      <span><b>Ręcznie</b> — dodana lub później zmieniona przez planującego.</span>
      <span><b>Otwarta zmiana</b> — generator pozostawił nieobsadzone miejsce.</span>
      <span><b>Wymiana</b> — wynik procesu wymiany zmiany.</span>
    </div>
  ),
  rules: (
    <div className="schedule-tooltip-content">
      <strong>Reguły i odstępstwa</strong>
      <span>Puste pole oznacza, że przy zmianie nie zapisano ręcznego odstępstwa od reguł czasu pracy.</span>
      <span><b>Odstępstwo</b> oznacza świadome zaakceptowanie reguły, którą system pozwala nadpisać, wraz z zapisanym uzasadnieniem.</span>
      <span>Twardych blokad, np. braku ważnej umowy lub kolizji zmian, nie można obejść odstępstwem.</span>
    </div>
  )
};

const dateTimeFormatter = new Intl.DateTimeFormat("pl-PL", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit"
});

const dayHeaderFormatter = new Intl.DateTimeFormat("pl-PL", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit"
});

const formatDateTime = (value) => value ? dateTimeFormatter.format(new Date(value)) : "";
const formatTime = (value) => value ? value.slice(11, 16) : "";
const assignmentDate = (value) => value ? value.slice(0, 10) : "";

const numericHours = (startsAt, endsAt) => {
  if (!startsAt || !endsAt) return "";
  const milliseconds = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  return Number((milliseconds / 3600000).toFixed(2));
};

const formatHours = (startsAt, endsAt) => {
  const hours = numericHours(startsAt, endsAt);
  return hours === "" ? "" : `${hours.toLocaleString("pl-PL", { maximumFractionDigits: 2 })} h`;
};

const parseLocalDate = (value) => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const dateToString = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const dateTimeToLocalString = (date) => `${dateToString(date)}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

const buildDateRange = (dateFrom, dateTo) => {
  const start = parseLocalDate(dateFrom);
  const end = parseLocalDate(dateTo);
  if (!start || !end || end < start) return [];
  const result = [];
  const current = new Date(start);
  while (current <= end) {
    result.push({ key: dateToString(current), label: dayHeaderFormatter.format(current) });
    current.setDate(current.getDate() + 1);
  }
  return result;
};

const clampDateString = (value, min, max) => {
  if (!value) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const startOfWeek = value => {
  const date = parseLocalDate(value);
  if (!date) return null;
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return date;
};

const endOfWeek = value => {
  const date = startOfWeek(value);
  if (!date) return null;
  date.setDate(date.getDate() + 6);
  return date;
};

const startOfMonth = value => {
  const date = parseLocalDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0, 0);
};

const endOfMonth = value => {
  const date = parseLocalDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0, 0);
};

const resolveVisibleRange = (schedule, view, anchor, customFrom, customTo) => {
  const safeAnchor = clampDateString(anchor, schedule.dateFrom, schedule.dateTo);
  let start;
  let end;

  if (view === "DAY") {
    start = parseLocalDate(safeAnchor);
    end = parseLocalDate(safeAnchor);
  } else if (view === "WEEK") {
    start = startOfWeek(safeAnchor);
    end = endOfWeek(safeAnchor);
  } else if (view === "BIWEEK") {
    start = startOfWeek(safeAnchor);
    end = new Date(start);
    end.setDate(end.getDate() + 13);
  } else if (view === "MONTH") {
    start = startOfMonth(safeAnchor);
    end = endOfMonth(safeAnchor);
  } else {
    start = parseLocalDate(clampDateString(customFrom, schedule.dateFrom, schedule.dateTo));
    end = parseLocalDate(clampDateString(customTo, schedule.dateFrom, schedule.dateTo));
  }

  const scheduleStart = parseLocalDate(schedule.dateFrom);
  const scheduleEnd = parseLocalDate(schedule.dateTo);
  if (!start || start < scheduleStart) start = scheduleStart;
  if (!end || end > scheduleEnd) end = scheduleEnd;
  if (end < start) end = new Date(start);
  return { dateFrom: dateToString(start), dateTo: dateToString(end) };
};

const shiftDateByView = (value, view, direction) => {
  const date = parseLocalDate(value);
  if (!date) return value;
  if (view === "MONTH") date.setMonth(date.getMonth() + direction);
  else if (view === "BIWEEK") date.setDate(date.getDate() + direction * 14);
  else if (view === "WEEK") date.setDate(date.getDate() + direction * 7);
  else date.setDate(date.getDate() + direction);
  return dateToString(date);
};

const dayOfWeekName = value => {
  const date = parseLocalDate(value);
  if (!date) return "";
  return ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"][date.getDay()];
};

const availabilityAppliesOn = (item, date) => {
  if (!item || !date || date < item.dateFrom || date > item.dateTo) return false;
  if (item.recurrence === "WEEKLY") return (item.daysOfWeek ?? []).includes(dayOfWeekName(date));
  return true;
};

const absenceAppliesOn = (item, date) => !!item && !!date && date >= item.dateFrom && date <= item.dateTo;

const isUnexcusedAbsence = item => {
  const shortName = String(item?.absenceType?.shortName || "").trim().toUpperCase();
  const name = String(item?.absenceType?.name || "").trim().toLocaleLowerCase("pl-PL");
  return shortName === "NN" || name.includes("nieusprawiedliw");
};

const rangeContainsDate = (date, dateFrom, dateTo) => date >= dateFrom && date <= dateTo;

const formatHourNumber = value => `${Number(value || 0).toLocaleString("pl-PL", { maximumFractionDigits: 2 })} h`;
const formatMinutesAsHours = value => `${(Number(value || 0) / 60).toLocaleString("pl-PL", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} h`;
const formatSignedMinutes = value => {
  const minutes = Number(value || 0);
  if (minutes === 0) return "0 min";
  return `${minutes > 0 ? "+" : ""}${minutes} min`;
};

const rcpStatusLabels = {
  FUTURE: "Przyszła zmiana",
  NOT_STARTED: "Oczekiwanie na rozpoczęcie",
  IN_PROGRESS: "W pracy",
  COMPLETED: "Zakończona",
  MISSING: "Brak rozpoczęcia RCP",
  INCOMPLETE: "Brak zakończenia RCP"
};

const consecutiveDaysContaining = (workDates, date) => {
  if (!workDates.has(date)) return 0;
  let count = 1;
  let cursor = parseLocalDate(date);
  cursor.setDate(cursor.getDate() - 1);
  while (workDates.has(dateToString(cursor))) {
    count += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  cursor = parseLocalDate(date);
  cursor.setDate(cursor.getDate() + 1);
  while (workDates.has(dateToString(cursor))) {
    count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const escapeCsv = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
const safeFileName = (value) => String(value || "grafik")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-zA-Z0-9._-]+/g, "_")
  .replace(/^_+|_+$/g, "") || "grafik";

const HelpHeader = ({ label, help }) => (
  <span className="schedule-column-header">
    <span>{label}</span>
    <OverlayTrigger placement="top" overlay={<Tooltip className="schedule-column-tooltip">{help}</Tooltip>}>
      <button type="button" className="schedule-help-trigger" aria-label={`Pomoc: ${label}`}>?</button>
    </OverlayTrigger>
  </span>
);

const ValueWithTooltip = ({ value, description, className = "" }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{description}</Tooltip>}>
    <span className={className}>{value}</span>
  </OverlayTrigger>
);

const ScheduleCollapsibleSection = ({ title, subtitle, meta, initiallyOpen = false, className = "", children }) => {
  const [open, setOpen] = useState(initiallyOpen);
  return (
    <details
      className={`schedule-section schedule-collapsible-panel ${className}`.trim()}
      open={open}
      onToggle={event => setOpen(event.currentTarget.open)}
    >
      <summary className="schedule-collapsible-panel-summary">
        <div className="schedule-collapsible-panel-title">
          <strong>{title}</strong>
          {subtitle && <span>{subtitle}</span>}
        </div>
        {meta != null && <span className="schedule-collapsible-panel-meta">{meta}</span>}
      </summary>
      <div className="schedule-collapsible-panel-body">{children}</div>
    </details>
  );
};

const WorkScheduleEditor = ({
  schedule,
  departments,
  referenceData,
  calendarData,
  generationPlans,
  generationResult,
  generationProgress,
  onGeneratePeriod,
  onCancelGeneration,
  onSaveSchedule,
  onPublish,
  onArchive,
  onDeleteSchedule,
  onSaveAssignment,
  onDeleteAssignment,
  onSaveAvailability,
  onDeleteAvailability,
  onSaveAbsence,
  onDeleteAbsence,
  onMarkUnexcusedAbsence,
  onRefreshCalendar,
  onDirectAssignmentChanged,
  onCopyAsDraft,
  saving,
  preferredGenerationPlanId
}) => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [form, setForm] = useState(schedule);
  const [editedAssignment, setEditedAssignment] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportFormat, setExportFormat] = useState("XLSX");
  const [exportTarget, setExportTarget] = useState("FILE");
  const [newAssignmentContext, setNewAssignmentContext] = useState(null);
  const [calendarView, setCalendarView] = useState("WEEK");
  const [hideUnassignedEmployees, setHideUnassignedEmployees] = useState(
    () => window.localStorage.getItem(HIDE_UNASSIGNED_STORAGE_KEY) === "1"
  );
  const [calendarAnchor, setCalendarAnchor] = useState(schedule.dateFrom);
  const [customDateFrom, setCustomDateFrom] = useState(schedule.dateFrom);
  const [customDateTo, setCustomDateTo] = useState(schedule.dateTo);
  const [calendarCell, setCalendarCell] = useState(null);
  const [calendarCellMode, setCalendarCellMode] = useState("MENU");
  const [editedAvailability, setEditedAvailability] = useState(null);
  const [editedAbsence, setEditedAbsence] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSending, setNotificationSending] = useState(false);
  const [notificationScope, setNotificationScope] = useState("ASSIGNED");
  const [notificationEmployeeId, setNotificationEmployeeId] = useState("");
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [employeeModalId, setEmployeeModalId] = useState(null);

  useEffect(() => {
    setForm(schedule);
    setEditedAssignment(null);
    setShowAssignmentModal(false);
    setShowDeleteConfirm(false);
    setShowExport(false);
    setNewAssignmentContext(null);
    setCalendarCell(null);
    setCalendarCellMode("MENU");
    setEditedAvailability(null);
    setEditedAbsence(null);
    setShowNotifications(false);
    setNotificationHistory([]);
    setNotificationScope("ASSIGNED");
    setNotificationEmployeeId("");
    setNotificationTitle("");
    setNotificationMessage("");
  }, [schedule]);

  useEffect(() => {
    setCalendarView("WEEK");
    setCalendarAnchor(schedule.dateFrom);
    setCustomDateFrom(schedule.dateFrom);
    setCustomDateTo(schedule.dateTo);
  }, [schedule.id, schedule.dateFrom, schedule.dateTo]);

  useEffect(() => {
    setEmployeeModalId(null);
  }, [schedule.id]);

  useEffect(() => {
    window.localStorage.setItem(HIDE_UNASSIGNED_STORAGE_KEY, hideUnassignedEmployees ? "1" : "0");
  }, [hideUnassignedEmployees]);

  const assignments = useMemo(
    () => [...(schedule.assignments ?? [])].sort((a, b) => (a.startsAt || "").localeCompare(b.startsAt || "")),
    [schedule.assignments]
  );

  const activeAssignments = useMemo(
    () => assignments.filter(item => item.status !== "CANCELLED"),
    [assignments]
  );

  const assignedShifts = useMemo(
    () => activeAssignments.filter(item => item.employeeId != null && item.employeeFullName),
    [activeAssignments]
  );

  const openShifts = useMemo(
    () => activeAssignments.filter(item => item.status === "OPEN" || item.employeeId == null || !item.employeeFullName),
    [activeAssignments]
  );

  const visibleRange = useMemo(
    () => resolveVisibleRange(schedule, calendarView, calendarAnchor, customDateFrom, customDateTo),
    [schedule, calendarView, calendarAnchor, customDateFrom, customDateTo]
  );

  const dateRange = useMemo(
    () => buildDateRange(visibleRange.dateFrom, visibleRange.dateTo),
    [visibleRange.dateFrom, visibleRange.dateTo]
  );

  const nonWorkingDayMap = useMemo(() => new Map(
    (calendarData?.nonWorkingDays ?? []).filter(item => item?.date).map(item => [item.date, item])
  ), [calendarData?.nonWorkingDays]);

  const visibleWeekStarts = useMemo(
    () => new Set(dateRange.map(day => dateToString(startOfWeek(day.key)))),
    [dateRange]
  );

  const calendarEmployees = useMemo(
    () => calendarData?.employees ?? referenceData?.employees ?? [],
    [calendarData?.employees, referenceData?.employees]
  );

  const calendarAvailabilities = useMemo(
    () => calendarData?.availabilities ?? [],
    [calendarData?.availabilities]
  );

  const calendarAbsences = useMemo(
    () => calendarData?.absences ?? [],
    [calendarData?.absences]
  );

  const workloadAssignments = useMemo(
    () => calendarData?.workloadAssignments ?? assignedShifts,
    [calendarData?.workloadAssignments, assignedShifts]
  );

  const attendanceComparisons = useMemo(
    () => calendarData?.attendanceComparisons ?? [],
    [calendarData?.attendanceComparisons]
  );

  const attendanceByAssignmentId = useMemo(() => {
    const result = new Map();
    attendanceComparisons.forEach(item => result.set(Number(item.assignmentId), item));
    return result;
  }, [attendanceComparisons]);

  const assignmentById = useMemo(() => {
    const result = new Map();
    assignments.forEach(item => result.set(Number(item.id), item));
    return result;
  }, [assignments]);

  const visibleAttendance = useMemo(
    () => attendanceComparisons.filter(item => item.date >= visibleRange.dateFrom && item.date <= visibleRange.dateTo),
    [attendanceComparisons, visibleRange.dateFrom, visibleRange.dateTo]
  );

  const rcpSummary = useMemo(() => visibleAttendance.reduce((summary, item) => {
    summary.scheduledMinutes += Number(item.plannedMinutes || 0);
    summary.plannedMinutes += Number(item.accountedPlannedMinutes || 0);
    if (item.workedMinutes != null) summary.workedMinutes += Number(item.workedMinutes || 0);
    summary.breakMinutes += Number(item.breakMinutes || 0);
    if (item.late) summary.lateCount += 1;
    if (item.earlyLeave) summary.earlyLeaveCount += 1;
    if (item.attendanceDataRisk) summary.attendanceDataRiskCount += 1;
    if (item.coverageRisk) {
      summary.coverageRiskCount += 1;
      summary.coverageRiskMinutes += Number(item.coverageRiskMinutes || 0);
    }
    if (item.status === "COMPLETED") summary.completedCount += 1;
    return summary;
  }, { scheduledMinutes: 0, plannedMinutes: 0, workedMinutes: 0, breakMinutes: 0, lateCount: 0, earlyLeaveCount: 0, attendanceDataRiskCount: 0, coverageRiskCount: 0, coverageRiskMinutes: 0, completedCount: 0 }), [visibleAttendance]);

  const employeeScheduleRows = useMemo(() => {
    const rows = new Map();

    calendarEmployees.forEach(employee => {
      rows.set(String(employee.id), {
        employeeId: employee.id,
        name: employee.fullName || `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim(),
        employee,
        totalHours: 0,
        visibleScheduleHours: 0,
        weekHours: 0,
        monthHours: 0,
        rcpVisibleMinutes: 0,
        rcpWeekMinutes: 0,
        rcpMonthMinutes: 0,
        rcpIssueCount: 0,
        maxConsecutiveVisible: 0,
        byDate: {},
        workloadByDate: {},
        workDates: new Set()
      });
    });

    assignedShifts.forEach(item => {
      const key = String(item.employeeId ?? item.employeeFullName);
      if (!rows.has(key)) {
        rows.set(key, {
          employeeId: item.employeeId,
          name: item.employeeFullName,
          employee: null,
          totalHours: 0,
          visibleScheduleHours: 0,
          weekHours: 0,
          monthHours: 0,
          rcpVisibleMinutes: 0,
          rcpWeekMinutes: 0,
          rcpMonthMinutes: 0,
          rcpIssueCount: 0,
          maxConsecutiveVisible: 0,
          byDate: {},
          workloadByDate: {},
          workDates: new Set()
        });
      }
      const row = rows.get(key);
      const date = assignmentDate(item.startsAt);
      if (!row.byDate[date]) row.byDate[date] = [];
      row.byDate[date].push(item);
      const hours = numericHours(item.startsAt, item.endsAt);
      if (hours !== "") row.totalHours += hours;
    });

    workloadAssignments.forEach(item => {
      if (item.employeeId == null || item.status === "CANCELLED") return;
      const key = String(item.employeeId);
      if (!rows.has(key)) return;
      const row = rows.get(key);
      const date = assignmentDate(item.startsAt);
      if (!date) return;
      if (!row.workloadByDate[date]) row.workloadByDate[date] = [];
      row.workloadByDate[date].push(item);
      row.workDates.add(date);
    });

    const summaryAnchor = calendarView === "CUSTOM" ? visibleRange.dateFrom : calendarAnchor;
    const safeAnchor = clampDateString(summaryAnchor, schedule.dateFrom, schedule.dateTo);
    const weekStartDate = startOfWeek(safeAnchor);
    const weekEndDate = endOfWeek(safeAnchor);
    const monthStartDate = startOfMonth(safeAnchor);
    const monthEndDate = endOfMonth(safeAnchor);
    const weekFrom = dateToString(weekStartDate);
    const weekTo = dateToString(weekEndDate);
    const monthFrom = dateToString(monthStartDate);
    const monthTo = dateToString(monthEndDate);

    rows.forEach(row => {
      attendanceComparisons.filter(item => Number(item.employeeId) === Number(row.employeeId)).forEach(item => {
        if (item.workedMinutes != null) {
          if (rangeContainsDate(item.date, visibleRange.dateFrom, visibleRange.dateTo)) row.rcpVisibleMinutes += Number(item.workedMinutes || 0);
          if (rangeContainsDate(item.date, weekFrom, weekTo)) row.rcpWeekMinutes += Number(item.workedMinutes || 0);
          if (rangeContainsDate(item.date, monthFrom, monthTo)) row.rcpMonthMinutes += Number(item.workedMinutes || 0);
        }
        if (rangeContainsDate(item.date, visibleRange.dateFrom, visibleRange.dateTo) && (item.attendanceDataRisk || item.coverageRisk || item.late || item.earlyLeave)) row.rcpIssueCount += 1;
      });
      Object.entries(row.workloadByDate).forEach(([date, items]) => {
        const hours = items.reduce((sum, item) => {
          const value = numericHours(item.startsAt, item.endsAt);
          return sum + (value === "" ? 0 : value);
        }, 0);
        if (rangeContainsDate(date, weekFrom, weekTo)) row.weekHours += hours;
        if (rangeContainsDate(date, monthFrom, monthTo)) row.monthHours += hours;
      });
      row.visibleScheduleHours = Object.entries(row.byDate).reduce((sum, [date, items]) => {
        if (!rangeContainsDate(date, visibleRange.dateFrom, visibleRange.dateTo)) return sum;
        return sum + items.reduce((daySum, item) => {
          const value = numericHours(item.startsAt, item.endsAt);
          return daySum + (value === "" ? 0 : value);
        }, 0);
      }, 0);
      row.maxConsecutiveVisible = dateRange.reduce(
        (max, day) => Math.max(max, consecutiveDaysContaining(row.workDates, day.key)),
        0
      );
    });

    return [...rows.values()].sort((a, b) => a.name.localeCompare(b.name, "pl"));
  }, [calendarEmployees, assignedShifts, workloadAssignments, attendanceComparisons, calendarView, calendarAnchor, visibleRange.dateFrom, visibleRange.dateTo, schedule.dateFrom, schedule.dateTo, dateRange]);

  const displayedEmployeeScheduleRows = useMemo(() => {
    if (!hideUnassignedEmployees) return employeeScheduleRows;
    const assignedEmployeeIds = new Set(
      assignedShifts
        .filter(item => rangeContainsDate(assignmentDate(item.startsAt), visibleRange.dateFrom, visibleRange.dateTo))
        .map(item => Number(item.employeeId))
    );
    return employeeScheduleRows.filter(row => row.employeeId != null && assignedEmployeeIds.has(Number(row.employeeId)));
  }, [employeeScheduleRows, assignedShifts, hideUnassignedEmployees, visibleRange.dateFrom, visibleRange.dateTo]);

  const unassignedEmployeeRowsCount = employeeScheduleRows.length - displayedEmployeeScheduleRows.length;

  const departmentDaySummary = useMemo(() => dateRange.map(day => {
    const shifts = assignedShifts.filter(item => assignmentDate(item.startsAt) === day.key);
    const plannedHours = shifts.reduce((sum, item) => {
      const value = numericHours(item.startsAt, item.endsAt);
      return sum + (value === "" ? 0 : value);
    }, 0);
    const employeeCount = new Set(shifts.map(item => item.employeeId).filter(id => id != null)).size;
    const attendance = attendanceComparisons.filter(item => item.date === day.key);
    const workedMinutes = attendance.reduce((sum, item) => sum + Number(item.workedMinutes || 0), 0);
    const rcpIssueCount = attendance.filter(item => item.attendanceDataRisk || item.coverageRisk || item.late || item.earlyLeave).length;
    const coverageRiskCount = attendance.filter(item => item.coverageRisk).length;
    const attendanceDataRiskCount = attendance.filter(item => item.attendanceDataRisk).length;
    return {
      date: day.key,
      plannedHours,
      employeeCount,
      workedMinutes,
      rcpIssueCount,
      coverageRiskCount,
      attendanceDataRiskCount
    };
  }), [dateRange, assignedShifts, attendanceComparisons]);

  const departmentVisibleSummary = useMemo(() => departmentDaySummary.reduce((summary, day) => {
    summary.plannedHours += day.plannedHours;
    summary.workedMinutes += day.workedMinutes;
    summary.rcpIssueCount += day.rcpIssueCount;
    summary.coverageRiskCount += day.coverageRiskCount;
    summary.attendanceDataRiskCount += day.attendanceDataRiskCount;
    return summary;
  }, { plannedHours: 0, workedMinutes: 0, rcpIssueCount: 0, coverageRiskCount: 0, attendanceDataRiskCount: 0 }), [departmentDaySummary]);

  const liveCoverageRisks = useMemo(
    () => visibleAttendance.filter(item => item.coverageRisk),
    [visibleAttendance]
  );

  const shortageGroups = useMemo(() => {
    const groups = new Map();
    openShifts.forEach(item => {
      const key = [assignmentDate(item.startsAt), item.positionName || "", item.startsAt || "", item.endsAt || ""].join("|");
      if (!groups.has(key)) {
        groups.set(key, {
          date: assignmentDate(item.startsAt),
          positionName: item.positionName || "Nieokreślone stanowisko",
          startsAt: item.startsAt,
          endsAt: item.endsAt,
          count: 0,
          firstAssignment: item
        });
      }
      groups.get(key).count += 1;
    });
    return [...groups.values()].sort((a, b) => (a.startsAt || "").localeCompare(b.startsAt || ""));
  }, [openShifts]);

  const exportRows = useMemo(
    () => activeAssignments.map(item => {
      const attendance = attendanceByAssignmentId.get(Number(item.id));
      return [
        item.employeeFullName || "Brak obsady",
        item.positionName || "",
        formatDateTime(item.startsAt),
        formatDateTime(item.endsAt),
        numericHours(item.startsAt, item.endsAt),
        item.hourlyRate == null ? "" : Number(item.hourlyRate).toFixed(2),
        costRateSourceLabels[item.costRateSource] ?? item.costRateSource ?? "",
        item.totalHourlyCost == null ? "" : Number(item.totalHourlyCost).toFixed(2),
        item.totalHourlyCost == null ? "" : Number((numericHours(item.startsAt, item.endsAt) * Number(item.totalHourlyCost)).toFixed(2)),
        attendance?.actualStart ? formatDateTime(attendance.actualStart) : "",
        attendance?.actualEnd ? formatDateTime(attendance.actualEnd) : "",
        attendance?.breakMinutes ?? "",
        attendance?.workedMinutes == null ? "" : Number((attendance.workedMinutes / 60).toFixed(2)),
        attendance?.deltaMinutes ?? "",
        attendance?.late ? attendance.lateMinutes : "",
        attendance?.earlyLeave ? attendance.earlyLeaveMinutes : "",
        attendance ? (rcpStatusLabels[attendance.status] ?? attendance.status) : "",
        assignmentStatusLabels[item.status] ?? item.status,
        assignmentSourceLabels[item.source] ?? item.source,
        item.ruleOverride ? (item.ruleOverrideReason || "Odstępstwo od reguł") : ""
      ];
    }),
    [activeAssignments, attendanceByAssignmentId]
  );

  const exportHeaders = ["Pracownik", "Stanowisko", "Początek plan", "Koniec plan", "Godziny plan", "Stawka/h", "Źródło stawki", "Koszt/h", "Koszt zmiany", "Początek RCP", "Koniec RCP", "Przerwy RCP min", "Godziny RCP", "Bilans min", "Spóźnienie min", "Wcześniejsze wyjście min", "Status RCP", "Status zmiany", "Źródło", "Reguły / odstępstwa"];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: name === "departmentId" ? Number(value) : value }));
  };

  const saveSchedule = (event) => {
    event.preventDefault();
    onSaveSchedule({
      name: form.name,
      departmentId: Number(form.departmentId),
      dateFrom: form.dateFrom,
      dateTo: form.dateTo,
      version: form.version
    });
  };

  const openNewAssignment = (context = null) => {
    setEditedAssignment(null);
    setNewAssignmentContext(context);
    setShowAssignmentModal(true);
  };

  const openEditAssignment = (assignment) => {
    setNewAssignmentContext(null);
    setEditedAssignment(assignment);
    setShowAssignmentModal(true);
  };

  const closeAssignmentModal = () => {
    if (saving) return;
    setShowAssignmentModal(false);
    setEditedAssignment(null);
    setNewAssignmentContext(null);
  };

  const saveAssignment = async (payload) => {
    const saved = await onSaveAssignment(editedAssignment, payload);
    if (saved) {
      setShowAssignmentModal(false);
      setEditedAssignment(null);
      setNewAssignmentContext(null);
    }
  };

  const deleteEditedAssignment = async () => {
    if (!editedAssignment?.id || saving) return;
    if (!window.confirm(`Usunąć zmianę ${formatDateTime(editedAssignment.startsAt)} – ${formatDateTime(editedAssignment.endsAt)}?`)) return;
    const deleted = await onDeleteAssignment(editedAssignment.id);
    if (deleted) {
      setShowAssignmentModal(false);
      setEditedAssignment(null);
      setNewAssignmentContext(null);
    }
  };

  const openCalendarCell = (row, date) => {
    if (schedule.status === "ARCHIVED") return;
    setCalendarCell({ employeeId: row.employeeId, name: row.name, date });
    setCalendarCellMode("MENU");
    setEditedAvailability(null);
    setEditedAbsence(null);
  };

  const closeCalendarCell = () => {
    if (saving) return;
    setCalendarCell(null);
    setCalendarCellMode("MENU");
    setEditedAvailability(null);
    setEditedAbsence(null);
  };

  const openAvailabilityForm = (item = null) => {
    if (item?.entrySource === "SEASONAL") return;
    setEditedAvailability(item);
    setCalendarCellMode("AVAILABILITY");
  };

  const openAbsenceForm = (item = null) => {
    setEditedAbsence(item);
    setCalendarCellMode("ABSENCE");
  };

  const openAssignmentFromCalendar = () => {
    const context = calendarCell ? { employeeId: calendarCell.employeeId, date: calendarCell.date } : null;
    setCalendarCell(null);
    setCalendarCellMode("MENU");
    openNewAssignment(context);
  };

  const changeCalendarPeriod = direction => {
    if (calendarView === "CUSTOM") return;
    const shifted = shiftDateByView(calendarAnchor, calendarView, direction);
    setCalendarAnchor(clampDateString(shifted, schedule.dateFrom, schedule.dateTo));
  };

  const cellAvailabilities = calendarCell
    ? calendarAvailabilities.filter(item => item.EID === calendarCell.employeeId && availabilityAppliesOn(item, calendarCell.date))
    : [];

  const cellAbsences = calendarCell
    ? calendarAbsences.filter(item => item.EID === calendarCell.employeeId && absenceAppliesOn(item, calendarCell.date))
    : [];

  const confirmDelete = async () => {
    await onDeleteSchedule();
    setShowDeleteConfirm(false);
  };

  const downloadBlob = (content, mime, extension) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeFileName(schedule.name)}_${schedule.dateFrom}_${schedule.dateTo}.${extension}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const buildCsv = () => {
    const rows = [exportHeaders, ...exportRows];
    return `\uFEFF${rows.map(row => row.map(escapeCsv).join(";")).join("\r\n")}`;
  };


  const loadNotificationHistory = async () => {
    setNotificationLoading(true);
    try {
      const response = await axiosPrivate.get(`/schedule-notifications/schedule/${schedule.id}`);
      setNotificationHistory(response.data ?? []);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać historii powiadomień.");
    } finally {
      setNotificationLoading(false);
    }
  };

  const openNotifications = async () => {
    setShowNotifications(true);
    await loadNotificationHistory();
  };

  const sendScheduleNotification = async () => {
    if (notificationScope === "EMPLOYEE" && !notificationEmployeeId) {
      toastError("Wybierz pracownika.");
      return;
    }
    setNotificationSending(true);
    try {
      await axiosPrivate.post(`/schedule-notifications/schedule/${schedule.id}/send`, {
        scope: notificationScope,
        employeeId: notificationScope === "EMPLOYEE" ? Number(notificationEmployeeId) : null,
        title: notificationTitle,
        message: notificationMessage
      });
      toastSuccess("Powiadomienia zostały utworzone i wysłane zgodnie z ustawieniami pracowników.");
      setNotificationTitle("");
      setNotificationMessage("");
      await loadNotificationHistory();
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się wysłać powiadomień.");
    } finally {
      setNotificationSending(false);
    }
  };

  const notificationStatusLabel = status => ({
    NOT_REQUESTED: "nie wybrano",
    QUEUED: "w kolejce",
    SENT: "wysłano",
    DELIVERED: "dostarczono",
    UNDELIVERED: "niedostarczono",
    FAILED: "błąd wysyłki",
    NOT_CONFIGURED: "brak konfiguracji",
    NO_ADDRESS: "brak adresu"
  }[status] || status || "");

  const notificationDateTime = value => value ? dateTimeFormatter.format(new Date(value)) : "—";

  const exportSchedule = async () => {
    try {
      if (exportTarget === "CLIPBOARD") {
        const delimiter = exportFormat === "CSV" ? ";" : "\t";
        const rows = [exportHeaders, ...exportRows];
        const text = rows.map(row => row.map(value => exportFormat === "CSV" ? escapeCsv(value) : String(value ?? "")).join(delimiter)).join("\r\n");
        await navigator.clipboard.writeText(text);
        toastSuccess("Dane grafiku skopiowano do schowka.");
      } else if (exportFormat === "CSV") {
        downloadBlob(buildCsv(), "text/csv;charset=utf-8", "csv");
        toastSuccess("Zapisano plik CSV.");
      } else {
        const worksheet = XLSX.utils.aoa_to_sheet([exportHeaders, ...exportRows]);
        worksheet["!cols"] = [
          {wch: 28}, {wch: 24}, {wch: 20}, {wch: 20}, {wch: 10},
          {wch: 12}, {wch: 22}, {wch: 12}, {wch: 14}, {wch: 14}, {wch: 18}, {wch: 28}
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Grafik");
        XLSX.writeFile(workbook, `${safeFileName(schedule.name)}_${schedule.dateFrom}_${schedule.dateTo}.xlsx`);
        toastSuccess("Zapisano plik Excel.");
      }
      setShowExport(false);
    } catch (error) {
      toastError("Nie udało się wyeksportować grafiku.");
    }
  };

  const employeeLink = (employeeId, label) => {
    if (employeeId == null) return label;
    return (
      <button
        type="button"
        className="schedule-employee-link"
        onClick={event => {
          event.stopPropagation();
          setEmployeeModalId(Number(employeeId));
        }}
        title="Otwórz kartotekę pracownika"
      >
        {label}
      </button>
    );
  };

  return (
    <div className="schedule-editor">
      <div className="schedule-editor-heading">
        <div>
          <h5 className="mb-1">{schedule.name}</h5>
          <span className={`schedule-status schedule-status-${schedule.status?.toLowerCase()}`}>{statusLabels[schedule.status] ?? schedule.status}</span>
        </div>
        <div className="schedule-form-actions">
          {schedule.status === "PUBLISHED" && onCopyAsDraft && <Button variant="outline-secondary" onClick={onCopyAsDraft} disabled={saving}>Utwórz kopię roboczą</Button>}
          {schedule.status === "PUBLISHED" && <Button variant="outline-primary" onClick={openNotifications} disabled={saving}>Powiadomienia</Button>}
          {schedule.status === "PUBLISHED" && <Button variant="outline-primary" onClick={() => setShowExport(true)} disabled={saving}>Eksportuj</Button>}
          {schedule.status === "DRAFT" && <Button variant="success" onClick={onPublish} disabled={saving}>Publikuj</Button>}
          {schedule.status !== "ARCHIVED" && <Button variant="outline-secondary" onClick={onArchive} disabled={saving}>Archiwizuj</Button>}
          {schedule.status === "DRAFT" && <Button variant="outline-danger" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>Usuń</Button>}
        </div>
      </div>

      <ScheduleCollapsibleSection
        title="Dane grafiku"
        subtitle="Nazwa, dział i zakres dat otwartego grafiku."
        initiallyOpen={true}
      >
        <form className="schedule-metadata-form" onSubmit={saveSchedule}>
          <Form.Group>
            <Form.Label>Nazwa</Form.Label>
            <Form.Control name="name" value={form.name ?? ""} onChange={handleChange} disabled={schedule.status === "ARCHIVED"} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Dział</Form.Label>
            <Form.Select name="departmentId" value={form.departmentId ?? ""} onChange={handleChange} disabled={assignments.length > 0 || schedule.status === "ARCHIVED"} required>
              {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Od</Form.Label>
            <Form.Control type="date" name="dateFrom" value={form.dateFrom ?? ""} onChange={handleChange} disabled={assignments.length > 0 || schedule.status === "ARCHIVED"} required />
          </Form.Group>
          <Form.Group>
            <Form.Label>Do</Form.Label>
            <Form.Control type="date" name="dateTo" value={form.dateTo ?? ""} onChange={handleChange} disabled={assignments.length > 0 || schedule.status === "ARCHIVED"} required />
          </Form.Group>
          <div className="schedule-metadata-save">
            <Button type="submit" variant="primary" disabled={saving || schedule.status === "ARCHIVED"}>Zapisz dane grafiku</Button>
          </div>
        </form>
      </ScheduleCollapsibleSection>

      <ScheduleCollapsibleSection
        title="Generowanie i planowanie obsady"
        subtitle={schedule.status === "DRAFT" ? "Generowanie zmian z planu zapotrzebowania oraz kontrola planu, kosztów i obsady." : "Planowanie obsady, kosztów i zapotrzebowania dla tego grafiku."}
        meta={`${assignedShifts.length} obsadzonych · ${openShifts.length} braków`}
        initiallyOpen={schedule.status === "DRAFT"}
      >
        {schedule.status === "DRAFT" && (
          <ScheduleGenerationPanel
            schedule={schedule}
            plans={generationPlans ?? []}
            result={generationResult}
            progress={generationProgress}
            onGenerate={onGeneratePeriod}
            onCancelGeneration={onCancelGeneration}
            saving={saving}
            preferredPlanId={preferredGenerationPlanId}
            defaultGenerationCriteria={referenceData?.defaultGenerationCriteria}
          />
        )}

        <WorkforcePlanningPanel
          schedule={schedule}
          visibleRange={visibleRange}
          assignments={assignments}
          onEditAssignment={openEditAssignment}
          onDirectAssignmentChanged={onDirectAssignmentChanged}
          departmentPlanningDefaults={referenceData}
        />
      </ScheduleCollapsibleSection>

      <ScheduleCollapsibleSection
        title="Grafik pracowników"
        subtitle="Kalendarz zmian, dostępności i nieobecności pracowników działu."
        meta={`${displayedEmployeeScheduleRows.length}/${employeeScheduleRows.length} pracowników · ${assignedShifts.length} zmian`}
        initiallyOpen={true}
        className="schedule-calendar-section"
      >
        <div className="schedule-section-actions schedule-section-actions-toolbar">
          <Form.Check
            type="switch"
            id={`schedule-hide-unassigned-${schedule.id}`}
            checked={hideUnassignedEmployees}
            onChange={event => setHideUnassignedEmployees(event.target.checked)}
            label={`Ukryj pracowników bez zmian w wybranym okresie${unassignedEmployeeRowsCount > 0 ? ` (${unassignedEmployeeRowsCount})` : ""}`}
          />
          {onRefreshCalendar && <Button size="sm" variant="outline-secondary" onClick={onRefreshCalendar} disabled={saving}>Odśwież RCP</Button>}
          {schedule.status !== "ARCHIVED" && <Button size="sm" variant="primary" onClick={() => openNewAssignment(null)}>Dodaj zmianę</Button>}
        </div>

        <div className={`schedule-rcp-summary ${rcpSummary.coverageRiskCount > 0 ? "schedule-rcp-summary-alert" : ""}`}>
          <span><b>Plan w zakresie:</b> {formatMinutesAsHours(rcpSummary.scheduledMinutes)}</span>
          <span><b>Plan do rozliczenia:</b> {formatMinutesAsHours(rcpSummary.plannedMinutes)}</span>
          <span><b>Wykonanie RCP:</b> {formatMinutesAsHours(rcpSummary.workedMinutes)}</span>
          <span className={rcpSummary.workedMinutes - rcpSummary.plannedMinutes > 0 ? "schedule-rcp-delta-plus" : rcpSummary.workedMinutes - rcpSummary.plannedMinutes < 0 ? "schedule-rcp-delta-minus" : ""}><b>Bilans:</b> {formatSignedMinutes(rcpSummary.workedMinutes - rcpSummary.plannedMinutes)}</span>
          <span><b>Przerwy:</b> {rcpSummary.breakMinutes} min</span>
          <span><b>Spóźnienia:</b> {rcpSummary.lateCount}</span>
          <span><b>Wcześniejsze wyjścia:</b> {rcpSummary.earlyLeaveCount}</span>
          <span className={rcpSummary.coverageRiskCount > 0 ? "schedule-rcp-risk-count" : ""}><b>Ryzyko braku obsady:</b> {rcpSummary.coverageRiskCount}</span>
          <span><b>Minuty zagrożone:</b> {rcpSummary.coverageRiskMinutes}</span>
          <span><b>Niepełne dane RCP:</b> {rcpSummary.attendanceDataRiskCount}</span>
        </div>

        <div className="schedule-calendar-toolbar">
          <div className="schedule-calendar-toolbar-nav">
            <Button size="sm" variant="outline-secondary" onClick={() => changeCalendarPeriod(-1)} disabled={calendarView === "CUSTOM"}>‹</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setCalendarAnchor(clampDateString(dateToString(new Date()), schedule.dateFrom, schedule.dateTo))} disabled={calendarView === "CUSTOM"}>Dzisiaj</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => changeCalendarPeriod(1)} disabled={calendarView === "CUSTOM"}>›</Button>
          </div>
          <Form.Select value={calendarView} onChange={event => setCalendarView(event.target.value)}>
            <option value="DAY">Dzień</option>
            <option value="WEEK">Tydzień</option>
            <option value="BIWEEK">2 tygodnie</option>
            <option value="MONTH">Miesiąc</option>
            <option value="CUSTOM">Dowolny zakres</option>
          </Form.Select>
          {calendarView === "CUSTOM" ? (
            <div className="schedule-calendar-custom-range">
              <Form.Control type="date" min={schedule.dateFrom} max={schedule.dateTo} value={customDateFrom} onChange={event => setCustomDateFrom(event.target.value)} />
              <span>–</span>
              <Form.Control type="date" min={customDateFrom || schedule.dateFrom} max={schedule.dateTo} value={customDateTo} onChange={event => setCustomDateTo(event.target.value)} />
            </div>
          ) : (
            <Form.Control type="date" min={schedule.dateFrom} max={schedule.dateTo} value={calendarAnchor} onChange={event => setCalendarAnchor(event.target.value)} />
          )}
          <span className="schedule-calendar-visible-range">{visibleRange.dateFrom} – {visibleRange.dateTo}</span>
        </div>

        {displayedEmployeeScheduleRows.length === 0 ? (
          <div className="schedule-empty">
            {employeeScheduleRows.length === 0
              ? "Brak pracowników przypisanych do tego działu."
              : "Wszyscy pracownicy bez zmian w wybranym okresie są ukryci."}
          </div>
        ) : (
          <div className="schedule-calendar-wrapper">
            <table className="schedule-calendar-table">
              <thead>
                <tr>
                  <th className="schedule-calendar-employee-column">Pracownik</th>
                  <th className="schedule-calendar-hours-column">Godziny</th>
                  {dateRange.map(day => {
                    const nonWorkingDay = nonWorkingDayMap.get(day.key);
                    return (
                      <th
                        key={day.key}
                        className={nonWorkingDay ? "schedule-calendar-day-non-working" : ""}
                        title={nonWorkingDay ? nonWorkingDay.name : undefined}
                      >
                        <span>{day.label}</span>
                        {nonWorkingDay && <small className="schedule-calendar-holiday-label">{nonWorkingDay.shortName || nonWorkingDay.name}</small>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayedEmployeeScheduleRows.map(row => {
                  const warningThreshold = calendarData?.consecutiveDaysWarningThreshold ?? 5;
                  const maximumConsecutive = row.employee?.scheduleMaxConsecutiveWorkDays ?? calendarData?.defaultMaxConsecutiveWorkDays ?? 7;
                  const minWeeklyHours = Number(row.employee?.scheduleMinWeeklyHours || 0);
                  const maxWeeklyHours = Number(row.employee?.scheduleMaxWeeklyHours || 0);
                  const weeklyHoursInVisibleWeeks = new Map();
                  Object.entries(row.workloadByDate).forEach(([date, items]) => {
                    const weekStart = dateToString(startOfWeek(date));
                    if (!visibleWeekStarts.has(weekStart)) return;
                    const hours = items.reduce((sum, item) => {
                      const value = numericHours(item.startsAt, item.endsAt);
                      return sum + (value === "" ? 0 : value);
                    }, 0);
                    weeklyHoursInVisibleWeeks.set(weekStart, Number(weeklyHoursInVisibleWeeks.get(weekStart) || 0) + hours);
                  });
                  const highestVisibleWeeklyHours = Math.max(0, ...weeklyHoursInVisibleWeeks.values());
                  const weeklyHoursClass = maxWeeklyHours > 0 && highestVisibleWeeklyHours > maxWeeklyHours
                    ? "schedule-hours-critical"
                    : maxWeeklyHours > 0 && highestVisibleWeeklyHours >= maxWeeklyHours * 0.9
                      ? "schedule-hours-warning"
                      : "";
                  const rowAlertClass = row.maxConsecutiveVisible >= maximumConsecutive
                    ? "schedule-calendar-employee-critical"
                    : row.maxConsecutiveVisible >= warningThreshold
                      ? "schedule-calendar-employee-warning"
                      : "";
                  return (
                    <tr key={row.employeeId ?? row.name}>
                      <th className={`schedule-calendar-employee-column ${rowAlertClass}`}>
                        {employeeLink(row.employeeId, row.name)}
                        {row.employeeId != null && visibleRange.dateFrom <= dateToString(new Date()) && visibleRange.dateTo >= dateToString(new Date()) && schedule.status !== "ARCHIVED" && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            className="ms-2 py-0 px-2"
                            title="Otwórz symulator odbicia RCP dla tego pracownika"
                            onClick={event => {
                              event.stopPropagation();
                              const params = new URLSearchParams({
                                simulateEmployeeId: String(row.employeeId),
                                simulateAt: dateTimeToLocalString(new Date())
                              });
                              navigate(`/app/rcp-events?${params.toString()}`);
                            }}
                          >
                            RCP
                          </Button>
                        )}
                        {row.maxConsecutiveVisible >= warningThreshold && (
                          <span className="schedule-calendar-streak-badge">{row.maxConsecutiveVisible} dni z rzędu</span>
                        )}
                        {row.rcpIssueCount > 0 && <span className="schedule-calendar-rcp-issue-badge">RCP: {row.rcpIssueCount}</span>}
                        {calendarAbsences.filter(item => item.EID === row.employeeId && isUnexcusedAbsence(item) && item.dateFrom <= visibleRange.dateTo && item.dateTo >= visibleRange.dateFrom).length > 0 && (
                          <span className="schedule-calendar-unexcused-count-badge">NN: {calendarAbsences.filter(item => item.EID === row.employeeId && isUnexcusedAbsence(item) && item.dateFrom <= visibleRange.dateTo && item.dateTo >= visibleRange.dateFrom).length}</span>
                        )}
                      </th>
                      <td className={`schedule-calendar-hours-column ${weeklyHoursClass}`}>
                        <span><b>Zakres:</b> {formatHourNumber(row.visibleScheduleHours)}</span>
                        <span><b>Tydz.:</b> {formatHourNumber(row.weekHours)}</span>
                        <span><b>Mies.:</b> {formatHourNumber(row.monthHours)}</span>
                        {(minWeeklyHours > 0 || maxWeeklyHours > 0) && (
                          <span className="schedule-hours-limit"><b>Norma tyg.:</b> {minWeeklyHours > 0 ? minWeeklyHours : "–"}–{maxWeeklyHours > 0 ? maxWeeklyHours : "–"} h</span>
                        )}
                        {maxWeeklyHours > 0 && highestVisibleWeeklyHours > maxWeeklyHours && (
                          <span className="schedule-hours-limit-alert">Przekroczono max o {formatHourNumber(highestVisibleWeeklyHours - maxWeeklyHours)}</span>
                        )}
                        {maxWeeklyHours > 0 && highestVisibleWeeklyHours <= maxWeeklyHours && highestVisibleWeeklyHours >= maxWeeklyHours * 0.9 && (
                          <span className="schedule-hours-limit-warning">Do max: {formatHourNumber(maxWeeklyHours - highestVisibleWeeklyHours)}</span>
                        )}
                        {minWeeklyHours > 0 && row.weekHours < minWeeklyHours && (
                          <span className="schedule-hours-limit-info">Do min w bieżącym tyg.: {formatHourNumber(minWeeklyHours - row.weekHours)}</span>
                        )}
                        <span className="schedule-calendar-hours-rcp"><b>RCP zakres:</b> {formatMinutesAsHours(row.rcpVisibleMinutes)}</span>
                        <span className="schedule-calendar-hours-rcp"><b>RCP tydz.:</b> {formatMinutesAsHours(row.rcpWeekMinutes)}</span>
                        <span className="schedule-calendar-hours-rcp"><b>RCP mies.:</b> {formatMinutesAsHours(row.rcpMonthMinutes)}</span>
                      </td>
                      {dateRange.map(day => {
                        const shifts = row.byDate[day.key] ?? [];
                        const workload = row.workloadByDate[day.key] ?? [];
                        const dailyHours = workload.reduce((sum, item) => {
                          const value = numericHours(item.startsAt, item.endsAt);
                          return sum + (value === "" ? 0 : value);
                        }, 0);
                        const availabilities = calendarAvailabilities.filter(item => item.EID === row.employeeId && availabilityAppliesOn(item, day.key));
                        const absences = calendarAbsences.filter(item => item.EID === row.employeeId && absenceAppliesOn(item, day.key));
                        const streak = consecutiveDaysContaining(row.workDates, day.key);
                        const shiftAttendance = shifts.map(item => attendanceByAssignmentId.get(Number(item.id))).filter(Boolean);
                        const actualDailyMinutes = shiftAttendance.reduce((sum, item) => sum + Number(item.workedMinutes || 0), 0);
                        const hasRcpExecution = shiftAttendance.some(item => item.workedMinutes != null);
                        const hasRcpRisk = shiftAttendance.some(item => item.coverageRisk);
                        const hasRcpDeviation = shiftAttendance.some(item => item.late || item.earlyLeave);
                        const hasUnavailable = availabilities.some(item => item.type === "UNAVAILABLE");
                        const hasPreferred = availabilities.some(item => item.type === "PREFERRED");
                        const definedNonWorkingDay = nonWorkingDayMap.get(day.key);
                        const cellClasses = [
                          shifts.length ? "schedule-calendar-cell-with-shift" : "",
                          absences.length ? "schedule-calendar-cell-absence" : "",
                          hasUnavailable ? "schedule-calendar-cell-unavailable" : "",
                          hasPreferred ? "schedule-calendar-cell-preferred" : "",
                          definedNonWorkingDay ? "schedule-calendar-cell-non-working" : "",
                          hasRcpRisk ? "schedule-calendar-cell-rcp-risk" : hasRcpDeviation ? "schedule-calendar-cell-rcp-deviation" : "",
                          streak >= maximumConsecutive ? "schedule-calendar-cell-critical" : streak >= warningThreshold ? "schedule-calendar-cell-warning" : "",
                          schedule.status !== "ARCHIVED" ? "schedule-calendar-cell-editable" : ""
                        ].filter(Boolean).join(" ");
                        return (
                          <td key={day.key} className={cellClasses} onClick={() => openCalendarCell(row, day.key)} title={definedNonWorkingDay?.name}>
                            {definedNonWorkingDay && (
                              <div className="schedule-calendar-holiday-cell-label">{definedNonWorkingDay.shortName || definedNonWorkingDay.name}</div>
                            )}
                            {absences.map(item => (
                              <button
                                key={`absence-${item.id}`}
                                type="button"
                                className={`schedule-calendar-absence-badge ${isUnexcusedAbsence(item) ? "schedule-calendar-absence-badge-unexcused" : ""}`}
                                style={item.absenceType?.color ? { borderLeftColor: item.absenceType.color } : undefined}
                                onClick={event => {
                                  event.stopPropagation();
                                  setCalendarCell({ employeeId: row.employeeId, name: row.name, date: day.key });
                                  openAbsenceForm(item);
                                }}
                                disabled={schedule.status === "ARCHIVED"}
                              >
                                {item.absenceType?.shortName || item.absenceType?.name || "Nieobecność"}
                              </button>
                            ))}
                            {availabilities.map(item => (
                              <button
                                key={`availability-${item.id}`}
                                type="button"
                                className={`schedule-calendar-availability-badge schedule-calendar-availability-${String(item.type || "AVAILABLE").toLowerCase()} ${item.entrySource === "SEASONAL" ? "schedule-calendar-availability-seasonal" : ""}`}
                                onClick={event => {
                                  event.stopPropagation();
                                  setCalendarCell({ employeeId: row.employeeId, name: row.name, date: day.key });
                                  openAvailabilityForm(item);
                                }}
                                disabled={schedule.status === "ARCHIVED" || item.entrySource === "SEASONAL"}
                                title={item.entrySource === "SEASONAL" ? `Sezonowa pełna dostępność${item.seasonalLabel ? `: ${item.seasonalLabel}` : ""}. Zarządzaj nią w Grafiku dostępności.` : undefined}
                              >
                                {item.entrySource === "SEASONAL" ? "Sezonowo dostępny" : item.type === "UNAVAILABLE" ? "Niedostępny" : item.type === "PREFERRED" ? "Preferowany" : "Dostępny"}
                                {(item.timeFrom || item.timeTo) && <span>{item.timeFrom?.slice(0, 5) || ""}–{item.timeTo?.slice(0, 5) || ""}</span>}
                                {item.entrySource === "SEASONAL" && item.seasonalLabel && <span>{item.seasonalLabel}</span>}
                              </button>
                            ))}
                            {shifts.map(item => {
                              const attendance = attendanceByAssignmentId.get(Number(item.id));
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  className={`schedule-calendar-shift ${attendance?.coverageRisk ? "schedule-calendar-shift-rcp-risk" : attendance?.late || attendance?.earlyLeave ? "schedule-calendar-shift-rcp-deviation" : ""}`}
                                  onClick={event => {
                                    event.stopPropagation();
                                    if (schedule.status !== "ARCHIVED") openEditAssignment(item);
                                  }}
                                  disabled={schedule.status === "ARCHIVED"}
                                  title={schedule.status !== "ARCHIVED" ? "Kliknij, aby edytować zmianę" : undefined}
                                >
                                  <strong>{formatTime(item.startsAt)}–{formatTime(item.endsAt)}</strong>
                                  <span>{item.positionName}</span>
                                  {attendance && attendance.status !== "FUTURE" && (
                                    <span className={`schedule-calendar-shift-rcp schedule-calendar-shift-rcp-${String(attendance.status || "").toLowerCase()}`}>
                                      <b>RCP:</b> {attendance.status === "MISSING" ? "brak rozpoczęcia" : attendance.status === "NOT_STARTED" ? "oczekiwanie" : `${formatTime(attendance.actualStart)}${attendance.actualEnd ? `–${formatTime(attendance.actualEnd)}` : "–…"}`}
                                      {attendance.workedMinutes != null && <em>{formatMinutesAsHours(attendance.workedMinutes)}</em>}
                                    </span>
                                  )}
                                  {attendance?.breakMinutes > 0 && <span className="schedule-calendar-shift-rcp-detail">Przerwy: {attendance.breakMinutes} min</span>}
                                  {attendance?.late && <span className="schedule-calendar-shift-rcp-alert">Spóźnienie: {attendance.lateMinutes} min</span>}
                                  {attendance?.earlyLeave && <span className="schedule-calendar-shift-rcp-alert">Wcześniejsze wyjście: {attendance.earlyLeaveMinutes} min</span>}
                                  {attendance?.deltaMinutes != null && <span className={`schedule-calendar-shift-rcp-delta ${attendance.deltaMinutes > 0 ? "plus" : attendance.deltaMinutes < 0 ? "minus" : ""}`}>Bilans {formatSignedMinutes(attendance.deltaMinutes)}</span>}
                                  {attendance?.coverageRisk && <span className="schedule-calendar-shift-rcp-risk-label">{rcpStatusLabels[attendance.status] ?? "Problem RCP"}</span>}
                                </button>
                              );
                            })}
                            <div className="schedule-calendar-cell-footer">
                              {dailyHours > 0 && <span>Plan {formatHourNumber(dailyHours)}</span>}
                              {hasRcpExecution && <span>RCP {formatMinutesAsHours(actualDailyMinutes)}</span>}
                              {streak >= warningThreshold && <span>{streak} dni z rzędu</span>}
                              {schedule.status !== "ARCHIVED" && <span className="schedule-calendar-cell-add">+</span>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="schedule-calendar-department-summary-row">
                  <th className="schedule-calendar-employee-column">Razem dział</th>
                  <td className="schedule-calendar-hours-column schedule-calendar-department-hours">
                    <span><b>Plan zakres:</b> {formatHourNumber(departmentVisibleSummary.plannedHours)}</span>
                    <span><b>RCP zakres:</b> {formatMinutesAsHours(departmentVisibleSummary.workedMinutes)}</span>
                    {departmentVisibleSummary.rcpIssueCount > 0 && <span className="schedule-calendar-department-issues"><b>Alerty RCP:</b> {departmentVisibleSummary.rcpIssueCount}</span>}
                    {departmentVisibleSummary.coverageRiskCount > 0 && <span className="schedule-calendar-department-issues"><b>Ryzyko obsady:</b> {departmentVisibleSummary.coverageRiskCount}</span>}
                  </td>
                  {departmentDaySummary.map(day => (
                    <td key={day.date} className={`schedule-calendar-department-day ${day.rcpIssueCount > 0 ? "schedule-calendar-department-day-alert" : ""}`}>
                      <strong>{formatHourNumber(day.plannedHours)}</strong>
                      <span>{day.employeeCount} os.</span>
                      {day.workedMinutes > 0 && <span>RCP {formatMinutesAsHours(day.workedMinutes)}</span>}
                      {day.rcpIssueCount > 0 && <span>RCP: {day.rcpIssueCount} alert.</span>}
                      {day.coverageRiskCount > 0 && <span>Brak obsady RCP: {day.coverageRiskCount}</span>}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </ScheduleCollapsibleSection>

      <details className="schedule-section schedule-technical-details schedule-rcp-details">
        <summary>
          <span>Plan vs wykonanie RCP</span>
          <span>{visibleAttendance.length}</span>
        </summary>
        <div className="schedule-technical-details-intro">Porównanie zaplanowanych zmian z rzeczywistymi odbiciami RCP w aktualnie widocznym zakresie kalendarza. Przy braku odbicia lub niepełnej sesji użyj „Reaguj”, aby otworzyć dzień pracownika i oznaczyć nieobecność albo skorygować obsadę.</div>
        {visibleAttendance.length === 0 ? (
          <div className="schedule-empty">Brak zmian do porównania w tym zakresie.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle schedule-rcp-comparison-table">
              <thead>
                <tr><th>Pracownik</th><th>Plan</th><th>RCP</th><th>Przerwy</th><th>Wykonanie</th><th>Bilans</th><th>Spóźnienie</th><th>Wcześniejsze wyjście</th><th>Status</th><th>Działanie</th></tr>
              </thead>
              <tbody>
                {visibleAttendance.map(item => {
                  const assignment = assignmentById.get(Number(item.assignmentId));
                  return (
                    <tr key={item.assignmentId} className={item.coverageRisk ? "schedule-rcp-comparison-risk" : item.late || item.earlyLeave ? "schedule-rcp-comparison-deviation" : ""}>
                      <td>{employeeLink(item.employeeId, assignment?.employeeFullName || `ID ${item.employeeId}`)}</td>
                      <td>{formatTime(item.scheduledStart)}–{formatTime(item.scheduledEnd)}</td>
                      <td>{item.actualStart ? `${formatTime(item.actualStart)}–${item.actualEnd ? formatTime(item.actualEnd) : "…"}` : "—"}</td>
                      <td>{item.breakMinutes || 0} min</td>
                      <td>{item.workedMinutes == null ? "—" : formatMinutesAsHours(item.workedMinutes)}</td>
                      <td className={item.deltaMinutes > 0 ? "schedule-rcp-delta-plus" : item.deltaMinutes < 0 ? "schedule-rcp-delta-minus" : ""}>{item.deltaMinutes == null ? "—" : formatSignedMinutes(item.deltaMinutes)}</td>
                      <td>{item.late ? `${item.lateMinutes} min` : "—"}</td>
                      <td>{item.earlyLeave ? `${item.earlyLeaveMinutes} min` : "—"}</td>
                      <td><span className={`schedule-rcp-status schedule-rcp-status-${String(item.status || "").toLowerCase()}`}>{rcpStatusLabels[item.status] ?? item.status}</span></td>
                      <td>
                        {(item.coverageRisk || item.late || item.earlyLeave) && schedule.status !== "ARCHIVED" ? (
                          <Button
                            size="sm"
                            variant={item.coverageRisk ? "outline-danger" : "outline-secondary"}
                            onClick={() => {
                              const row = employeeScheduleRows.find(entry => Number(entry.employeeId) === Number(item.employeeId));
                              if (row) openCalendarCell(row, item.date);
                            }}
                          >
                            {item.coverageRisk ? "Reaguj" : "Otwórz dzień"}
                          </Button>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </details>

      {liveCoverageRisks.length > 0 && (
        <details className="schedule-section schedule-technical-details schedule-shortages-section schedule-rcp-summary-alert">
          <summary>
            <span>Braki i ryzyko obsady wynikające z RCP</span>
            <span className="schedule-shortage-count">{liveCoverageRisks.length}</span>
          </summary>
          <div className="schedule-technical-details-intro">Spóźnienie, brak rozpoczęcia pracy albo wcześniejsze wyjście oznaczają rzeczywisty ubytek obsady względem grafiku.</div>
          <div className="table-responsive">
            <table className="table table-sm align-middle schedule-shortage-table">
              <thead>
                <tr><th>Dzień</th><th>Pracownik</th><th>Plan</th><th>Powód</th><th>Zagrożenie</th><th></th></tr>
              </thead>
              <tbody>
                {liveCoverageRisks.map(item => {
                  const assignment = assignmentById.get(Number(item.assignmentId));
                  const reasons = [];
                  if (item.missingStart) reasons.push("Brak odbicia rozpoczęcia pracy");
                  if (item.late) reasons.push(`Spóźnienie o ${item.lateMinutes || 0} min`);
                  if (item.earlyLeave) reasons.push(`Wcześniejsze wyjście o ${item.earlyLeaveMinutes || 0} min`);
                  const reason = reasons.length ? reasons.join(" · ") : "Ryzyko obsady";
                  return (
                    <tr key={`coverage-${item.assignmentId}`} className="schedule-rcp-comparison-risk">
                      <td>{item.date}</td>
                      <td>{employeeLink(item.employeeId, assignment?.employeeFullName || `ID ${item.employeeId}`)}</td>
                      <td>{formatTime(item.scheduledStart)}–{formatTime(item.scheduledEnd)}</td>
                      <td>{reason}</td>
                      <td>{item.coverageRiskMinutes || 0} min</td>
                      <td className="text-end">
                        {schedule.status !== "ARCHIVED" && <Button size="sm" variant="outline-danger" onClick={() => {
                          const row = employeeScheduleRows.find(entry => Number(entry.employeeId) === Number(item.employeeId));
                          if (row) openCalendarCell(row, item.date);
                        }}>Reaguj</Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {openShifts.length > 0 && (
        <details className="schedule-section schedule-technical-details schedule-shortages-section">
          <summary>
            <span>Braki obsady / otwarte zmiany</span>
            <span className="schedule-shortage-count">{openShifts.length}</span>
          </summary>
          <div className="schedule-technical-details-intro">Każda taka pozycja oznacza zapotrzebowanie, dla którego nie ma jeszcze przypisanego pracownika.</div>
          <div className="table-responsive">
            <table className="table table-sm align-middle schedule-shortage-table">
              <thead>
                <tr><th>Dzień</th><th>Stanowisko</th><th>Godziny</th><th>Liczba braków</th><th></th></tr>
              </thead>
              <tbody>
                {shortageGroups.map(group => (
                  <tr key={`${group.date}-${group.positionName}-${group.startsAt}-${group.endsAt}`}>
                    <td>{group.date}</td>
                    <td>{group.positionName}</td>
                    <td>{formatTime(group.startsAt)}–{formatTime(group.endsAt)}</td>
                    <td><strong>{group.count}</strong></td>
                    <td className="text-end">
                      {schedule.status !== "ARCHIVED" && <Button size="sm" variant="outline-primary" onClick={() => openEditAssignment(group.firstAssignment)}>Obsadź / edytuj</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      <details className="schedule-section schedule-technical-details">
        <summary>
          <span>Szczegóły wszystkich zmian</span>
          <span>{assignments.length}</span>
        </summary>
        <div className="schedule-technical-details-intro">Widok techniczny pokazuje również otwarte i anulowane pozycje oraz informacje o ich pochodzeniu i odstępstwach od reguł.</div>
        {assignments.length === 0 ? (
          <div className="schedule-empty">Brak pozycji.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle schedule-table">
              <thead>
                <tr>
                  <th>Pracownik</th>
                  <th>Stanowisko</th>
                  <th>Początek</th>
                  <th>Koniec</th>
                  <th>Godziny</th>
                  <th>Stawka/h</th>
                  <th>Źródło stawki</th>
                  <th>Koszt/h</th>
                  <th>Koszt zmiany</th>
                  <th><HelpHeader label="Status" help={columnHelp.status} /></th>
                  <th><HelpHeader label="Źródło" help={columnHelp.source} /></th>
                  <th><HelpHeader label="Reguły / odstępstwa" help={columnHelp.rules} /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {assignments.map(item => (
                  <tr key={item.id} className={item.status === "CANCELLED" ? "schedule-row-cancelled" : ""}>
                    <td>{item.employeeFullName || <span className="schedule-open-label">Brak obsady</span>}</td>
                    <td>{item.positionName}</td>
                    <td>{formatDateTime(item.startsAt)}</td>
                    <td>{formatDateTime(item.endsAt)}</td>
                    <td>{formatHours(item.startsAt, item.endsAt)}</td>
                    <td>{item.hourlyRate == null ? "" : `${Number(item.hourlyRate).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`}</td>
                    <td>{costRateSourceLabels[item.costRateSource] ?? item.costRateSource ?? "—"}</td>
                    <td>{item.totalHourlyCost == null ? <span title={item.costComplete === false ? "Koszt niepełny — sprawdź konfigurację ZUS/KRUS" : ""}>—</span> : `${Number(item.totalHourlyCost).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`}</td>
                    <td>{item.totalHourlyCost == null ? "—" : `${(numericHours(item.startsAt, item.endsAt) * Number(item.totalHourlyCost)).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`}</td>
                    <td>
                      <ValueWithTooltip
                        value={assignmentStatusLabels[item.status] ?? item.status}
                        description={assignmentStatusDescriptions[item.status] ?? "Status tej pozycji grafiku."}
                        className={`schedule-assignment-status schedule-assignment-status-${String(item.status || "").toLowerCase()}`}
                      />
                    </td>
                    <td>
                      <ValueWithTooltip
                        value={assignmentSourceLabels[item.source] ?? item.source}
                        description={assignmentSourceDescriptions[item.source] ?? "Źródło utworzenia tej pozycji grafiku."}
                        className="schedule-assignment-source"
                      />
                    </td>
                    <td>
                      {item.ruleOverride ? (
                        <ValueWithTooltip
                          value="Odstępstwo"
                          description={item.ruleOverrideReason || "Ręczne odstępstwo od reguł czasu pracy."}
                          className="schedule-rule-override-badge"
                        />
                      ) : <span className="schedule-no-override">Brak</span>}
                    </td>
                    <td className="schedule-row-actions">
                      {schedule.status !== "ARCHIVED" && <Button size="sm" variant="outline-primary" onClick={() => openEditAssignment(item)}>Edytuj</Button>}
                      {schedule.status !== "ARCHIVED" && <Button size="sm" variant="outline-danger" onClick={() => onDeleteAssignment(item.id)}>Usuń</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>

      <Modal show={!!calendarCell} onHide={closeCalendarCell} centered scrollable backdrop="static" className="schedule-calendar-cell-modal">
        <Modal.Header closeButton>
          <Modal.Title>{calendarCell?.name} · {calendarCell?.date}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {calendarCellMode === "MENU" && (
            <div className="schedule-calendar-cell-menu">
              <div className="schedule-calendar-cell-actions">
                <Button variant="primary" onClick={openAssignmentFromCalendar}>Dodaj zmianę</Button>
                <Button variant="outline-primary" onClick={() => openAvailabilityForm(null)}>Dodaj dostępność / niedostępność</Button>
                <Button variant="outline-primary" onClick={() => openAbsenceForm(null)}>Dodaj nieobecność</Button>
                <Button variant="outline-danger" onClick={() => setCalendarCellMode("UNEXCUSED")} disabled={cellAbsences.some(isUnexcusedAbsence)}>Nieobecność nieusprawiedliwiona</Button>
              </div>

              <div className="schedule-calendar-cell-existing">
                <h6>Dostępność</h6>
                {cellAvailabilities.length === 0 ? (
                  <div className="schedule-calendar-cell-empty">Brak dodatkowych wpisów dostępności dla tego dnia.</div>
                ) : cellAvailabilities.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    className={`schedule-calendar-cell-existing-item ${item.entrySource === "SEASONAL" ? "schedule-calendar-cell-existing-seasonal" : ""}`}
                    onClick={() => openAvailabilityForm(item)}
                    disabled={item.entrySource === "SEASONAL"}
                    title={item.entrySource === "SEASONAL" ? "Sezonową dostępnością zarządza panel Sterowanie sezonowością w Grafiku dostępności." : undefined}
                  >
                    <strong>{item.entrySource === "SEASONAL" ? "Sezonowa pełna dostępność" : item.type === "UNAVAILABLE" ? "Niedostępny" : item.type === "PREFERRED" ? "Preferowana dostępność" : "Dostępny"}</strong>
                    <span>{item.entrySource === "SEASONAL" ? `${item.dateFrom}–${item.dateTo}` : `${item.timeFrom ? item.timeFrom.slice(0, 5) : "cały dzień"}${item.timeTo ? `–${item.timeTo.slice(0, 5)}` : ""}`}</span>
                    {item.seasonalLabel && <small>{item.seasonalLabel}</small>}
                    {item.comments && <small>{item.comments}</small>}
                  </button>
                ))}
              </div>

              <div className="schedule-calendar-cell-existing">
                <h6>Nieobecności</h6>
                {cellAbsences.length === 0 ? (
                  <div className="schedule-calendar-cell-empty">Brak nieobecności dla tego dnia.</div>
                ) : cellAbsences.map(item => (
                  <button key={item.id} type="button" className={`schedule-calendar-cell-existing-item ${isUnexcusedAbsence(item) ? "schedule-calendar-cell-existing-unexcused" : ""}`} onClick={() => openAbsenceForm(item)}>
                    <strong>{item.absenceType?.name || "Nieobecność"}</strong>
                    <span>{item.dateFrom}–{item.dateTo}</span>
                    {item.comments && <small>{item.comments}</small>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {calendarCellMode === "AVAILABILITY" && calendarCell && (
            <ScheduleAvailabilityForm
              employeeId={calendarCell.employeeId}
              date={calendarCell.date}
              availability={editedAvailability}
              onSave={onSaveAvailability}
              onDelete={onDeleteAvailability}
              onCancel={() => { setCalendarCellMode("MENU"); setEditedAvailability(null); }}
              saving={saving}
            />
          )}

          {calendarCellMode === "UNEXCUSED" && calendarCell && (
            <ScheduleUnexcusedAbsenceForm
              employeeId={calendarCell.employeeId}
              date={calendarCell.date}
              supervisors={calendarData?.supervisors ?? []}
              onSave={onMarkUnexcusedAbsence}
              onCancel={() => setCalendarCellMode("MENU")}
              saving={saving}
            />
          )}

          {calendarCellMode === "ABSENCE" && calendarCell && (
            <ScheduleAbsenceForm
              employeeId={calendarCell.employeeId}
              date={calendarCell.date}
              absence={editedAbsence}
              absenceTypes={calendarData?.absenceTypes ?? []}
              supervisors={calendarData?.supervisors ?? []}
              onSave={onSaveAbsence}
              onDelete={onDeleteAbsence}
              onCancel={() => { setCalendarCellMode("MENU"); setEditedAbsence(null); }}
              saving={saving}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showAssignmentModal} onHide={closeAssignmentModal} size="xl" centered scrollable backdrop="static" className="schedule-assignment-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editedAssignment ? "Edycja zmiany" : "Nowa zmiana"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editedAssignment?.status === "OPEN" && (
            <div className="schedule-open-edit-info">
              <strong>To jest brak obsady.</strong>
              <span>Aby przypisać pracownika, zmień Status z „Otwarta” na „Zaplanowana”, a następnie wybierz pracownika z listy.</span>
            </div>
          )}
          <ShiftAssignmentForm
            schedule={schedule}
            referenceData={referenceData}
            assignment={editedAssignment}
            initialEmployeeId={newAssignmentContext?.employeeId}
            initialDate={newAssignmentContext?.date}
            onSave={saveAssignment}
            onCancel={closeAssignmentModal}
            saving={saving}
          />
        </Modal.Body>
        {editedAssignment && schedule.status !== "ARCHIVED" && (
          <Modal.Footer className="justify-content-start">
            <Button variant="outline-danger" disabled={saving} onClick={deleteEditedAssignment}>Usuń zmianę</Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal
        show={employeeModalId != null}
        onHide={() => setEmployeeModalId(null)}
        size="xl"
        centered
        scrollable
        className="schedule-employee-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Kartoteka pracownika</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {employeeModalId != null && (
            <Employee
              employeeId={employeeModalId}
              embedded
              onEmbeddedClose={() => setEmployeeModalId(null)}
              onEmbeddedSaved={() => {
                if (typeof onRefreshCalendar === "function") onRefreshCalendar();
              }}
            />
          )}
        </Modal.Body>
      </Modal>


      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered className="schedule-delete-modal">
        <Modal.Header closeButton className="schedule-delete-modal-header">
          <Modal.Title>Usunąć grafik roboczy?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="schedule-delete-modal-name">{schedule.name}</div>
          <div className="schedule-delete-modal-period">{schedule.dateFrom} – {schedule.dateTo}</div>
          <p className="mb-0 mt-3">Operacja usunie grafik oraz wszystkie jego zmiany i nie będzie można jej cofnąć.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>Anuluj</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={saving}>{saving ? "Usuwanie..." : "Usuń grafik"}</Button>
        </Modal.Footer>
      </Modal>


      <Modal show={showNotifications} onHide={() => setShowNotifications(false)} size="xl" centered scrollable className="schedule-notifications-manager-modal">
        <Modal.Header closeButton>
          <Modal.Title>Powiadomienia · {schedule.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="schedule-notification-send-grid">
            <Form.Group>
              <Form.Label>Odbiorcy</Form.Label>
              <Form.Select value={notificationScope} onChange={event => setNotificationScope(event.target.value)}>
                <option value="ASSIGNED">Pracownicy mający zmianę w tym grafiku</option>
                <option value="DEPARTMENT">Wszyscy pracownicy działu</option>
                <option value="EMPLOYEE">Jeden pracownik</option>
              </Form.Select>
            </Form.Group>
            {notificationScope === "EMPLOYEE" && (
              <Form.Group>
                <Form.Label>Pracownik</Form.Label>
                <Form.Select value={notificationEmployeeId} onChange={event => setNotificationEmployeeId(event.target.value)}>
                  <option value="">-- wybierz --</option>
                  {calendarEmployees.map(employee => (
                    <option key={employee.id} value={employee.id}>{employee.fullName || `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim()}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group>
              <Form.Label>Tytuł</Form.Label>
              <Form.Control value={notificationTitle} onChange={event => setNotificationTitle(event.target.value)} placeholder={`Informacja o grafiku: ${schedule.name}`} />
            </Form.Group>
            <Form.Group className="schedule-notification-message-field">
              <Form.Label>Wiadomość</Form.Label>
              <Form.Control as="textarea" rows={3} value={notificationMessage} onChange={event => setNotificationMessage(event.target.value)} placeholder="Jeśli pozostawisz puste, system użyje standardowej informacji o aktualnym grafiku." />
            </Form.Group>
          </div>
          <div className="d-flex justify-content-end mb-3">
            <Button variant="primary" onClick={sendScheduleNotification} disabled={notificationSending}>
              {notificationSending ? "Wysyłanie..." : "Wyślij powiadomienia"}
            </Button>
          </div>

          <h6>Historia i potwierdzenia odbioru</h6>
          {notificationLoading ? (
            <div className="schedule-empty">Ładowanie...</div>
          ) : notificationHistory.length === 0 ? (
            <div className="schedule-empty">Brak powiadomień dla tego grafiku.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle schedule-notification-history-table">
                <thead>
                  <tr><th>Data</th><th>Pracownik</th><th>Treść</th><th>Email</th><th>SMS</th><th>Odczyt</th><th>Potwierdzenie</th></tr>
                </thead>
                <tbody>
                  {notificationHistory.map(item => (
                    <tr key={item.id}>
                      <td>{notificationDateTime(item.createdAt)}</td>
                      <td>{item.employeeFullName}</td>
                      <td><strong>{item.title}</strong><div>{item.message}</div></td>
                      <td>{notificationStatusLabel(item.emailStatus)}{item.emailSentAt && <div>{notificationDateTime(item.emailSentAt)}</div>}</td>
                      <td>
                        {notificationStatusLabel(item.smsStatus)}
                        {item.smsDeliveredAt && <div>Dostarczono: {notificationDateTime(item.smsDeliveredAt)}</div>}
                        {item.smsProviderStatus && <div>Operator: {item.smsProviderStatus}</div>}
                      </td>
                      <td>{notificationDateTime(item.readAt)}</td>
                      <td>{notificationDateTime(item.acknowledgedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => navigate("/app/schedule-notifications")}>Centrum powiadomień</Button>
          <Button variant="secondary" onClick={() => setShowNotifications(false)}>Zamknij</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showExport} onHide={() => setShowExport(false)} centered className="schedule-export-modal">
        <Modal.Header closeButton>
          <Modal.Title>Eksport opublikowanego grafiku</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="schedule-export-grid">
            <Form.Group>
              <Form.Label>Format</Form.Label>
              <Form.Select value={exportFormat} onChange={event => setExportFormat(event.target.value)}>
                <option value="XLSX">Excel (.xlsx)</option>
                <option value="CSV">CSV (.csv)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Miejsce docelowe</Form.Label>
              <Form.Select value={exportTarget} onChange={event => setExportTarget(event.target.value)}>
                <option value="FILE">Plik</option>
                <option value="CLIPBOARD">Schowek</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="schedule-export-info">Eksport obejmuje {exportRows.length} aktywnych pozycji. Zmiany anulowane są pomijane.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExport(false)}>Anuluj</Button>
          <Button variant="primary" onClick={exportSchedule}>Eksportuj</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WorkScheduleEditor;




